import { z } from 'zod/v4';

import { MonetaryValueBackendRuntype } from '../backendTypes';
import { PriceTypeRuntype } from '../offer/offerBackendTypes';
import { PackagingRuntype } from '../offer/Packaging';
import { SolutionStatusRuntype } from '../solution/solutionBackendTypes';

export enum RuleBasedSelectionFallback {
    KeepCurrent = 'KeepCurrent',
    SelectNothing = 'SelectNothing',
}

export enum RuleBasedSelectionPartSpecification {
    OffTheShelf = 'OffTheShelf',
    Custom = 'Custom',
}

export enum SolutionStatusPredicateOp {
    GreaterEq = 'GreaterEq',
}

export enum ArrayContainsOp {
    IsIn = 'IsIn',
    IsNotIn = 'IsNotIn',
}

export enum EqualityOp {
    Is = 'Is',
    IsNot = 'IsNot',
}

export enum PriceComparisonPredicateOp {
    AtMostMoreExpensive = 'AtMostMoreExpensive',
    AtLeastCheaper = 'AtLeastCheaper',
}

export enum ExcessCostComparisonPredicateOp {
    AtMostGreater = 'AtMostGreater',
}

const ArrayContainsOpRuntype = z.enum(ArrayContainsOp);

const thresholdParamsRuntype = z.object({
    threshold_relative: z.number(),
    threshold_absolute: MonetaryValueBackendRuntype,
});

const PriceComparisonPredicateRuntype = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('ComparisonByFirstSolution'),
        value: thresholdParamsRuntype,
        op: z.enum(PriceComparisonPredicateOp),
    }),
    z.object({
        kind: z.literal('ComparisonBySelectedSolution'),
        value: thresholdParamsRuntype,
        op: z.enum(PriceComparisonPredicateOp),
    }),
]);

const SupplierPredicateDTORuntype = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('Contains'),
        values: z.array(z.string()),
        op: ArrayContainsOpRuntype,
    }),
    z.object({
        kind: z.literal('EqualityBySelectedSolution'),
        op: z.enum(EqualityOp),
    }),
]);

const ManufacturerPredicateDTORuntype = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('Contains'),
        values: z.array(z.string()),
        op: ArrayContainsOpRuntype,
    }),
    z.object({
        kind: z.literal('EqualityBySelectedSolution'),
        op: z.enum(EqualityOp),
    }),
]);

export type RuleBasedSelectionPredicateDTO = z.infer<typeof RuleBasedSelectionPredicateDTORuntype>;
export const RuleBasedSelectionPredicateDTORuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('Supplier'),
        data: SupplierPredicateDTORuntype,
    }),
    z.object({
        type: z.literal('Manufacturer'),
        data: ManufacturerPredicateDTORuntype,
    }),
    z.object({
        type: z.literal('SolutionStatusComparison'),
        data: z.object({
            value: SolutionStatusRuntype,
            op: z.enum(SolutionStatusPredicateOp),
        }),
    }),
    z.object({
        type: z.literal('PartSpecificationEquality'),
        data: z.object({
            value: z.enum(RuleBasedSelectionPartSpecification),
            op: z.enum(EqualityOp),
        }),
    }),
    z.object({
        type: z.literal('PackagingContains'),
        data: z.object({
            values: z.array(PackagingRuntype),
            op: ArrayContainsOpRuntype,
            includes_unknown: z.boolean(),
        }),
    }),
    z.object({
        type: z.literal('PriceTypeContains'),
        data: z.object({
            values: z.array(PriceTypeRuntype),
            op: ArrayContainsOpRuntype,
        }),
    }),
    z.object({
        type: z.literal('TotalPurchasePrice'),
        data: PriceComparisonPredicateRuntype,
    }),
    z.object({
        type: z.literal('TotalPrice'),
        data: PriceComparisonPredicateRuntype,
    }),
    z.object({
        type: z.literal('ExcessCostComparison'),
        data: z.object({
            value: thresholdParamsRuntype,
            op: z.enum(ExcessCostComparisonPredicateOp),
        }),
    }),
]);

export type RuleBasedSelectionConditionDTO = z.infer<typeof RuleBasedSelectionConditionDTORuntype>;
export const RuleBasedSelectionConditionDTORuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('And'),
        data: z.array(RuleBasedSelectionPredicateDTORuntype),
    }),
    z.object({
        type: z.literal('Predicate'),
        data: RuleBasedSelectionPredicateDTORuntype,
    }),
]);

export const RuleBasedSelectionFallbackRuntype = z.enum(RuleBasedSelectionFallback);
